import { useEffect, useId, useRef, useState } from "react";
import { ClientApiError } from "../auth/api";
import { searchPublicTaxonomy, type PublicTaxonomyItem } from "./api";

export function TaxonomyAutocomplete({
  namespace,
  label,
  placeholder,
  hint,
  value,
  onSelect,
}: {
  namespace: string;
  label: string;
  placeholder?: string;
  hint?: string;
  value?: PublicTaxonomyItem | null;
  onSelect: (item: PublicTaxonomyItem | null) => void;
}) {
  const inputId = useId();
  const listboxId = useId();
  const blurTimer = useRef<number | null>(null);
  const [query, setQuery] = useState(value?.nameVi ?? "");
  const [items, setItems] = useState<PublicTaxonomyItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    setQuery(value?.nameVi ?? "");
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await searchPublicTaxonomy(namespace, {
          query: query.trim(),
          limit: 12,
        });
        if (controller.signal.aborted) return;
        setItems(response.items);
        setRequestId(null);
        setActiveIndex(response.items.length > 0 ? 0 : -1);
      } catch (caught) {
        if (controller.signal.aborted) return;
        setItems([]);
        setActiveIndex(-1);
        if (caught instanceof ClientApiError) {
          setError(caught.problem.message);
          setRequestId(caught.problem.requestId);
        } else {
          setError("Không thể tải danh mục.");
          setRequestId(null);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 250);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [namespace, open, query]);

  function choose(item: PublicTaxonomyItem) {
    setQuery(item.nameVi);
    setOpen(false);
    setActiveIndex(-1);
    onSelect(item);
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) => Math.min(items.length - 1, current + 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) => Math.max(0, current - 1));
    } else if (event.key === "Enter" && open && activeIndex >= 0 && items[activeIndex]) {
      event.preventDefault();
      choose(items[activeIndex]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      setActiveIndex(-1);
    }
  }

  return (
    <div className="taxonomy-autocomplete">
      <label className="ui-field" htmlFor={inputId}>
        <span className="ui-field__label">{label}</span>
        <div className="taxonomy-autocomplete__control">
          <input
            id={inputId}
            className="ui-control"
            type="search"
            role="combobox"
            autoComplete="off"
            placeholder={placeholder}
            value={query}
            aria-expanded={open}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={
              open && activeIndex >= 0 && items[activeIndex]
                ? `${listboxId}-${items[activeIndex].id}`
                : undefined
            }
            onFocus={() => {
              if (blurTimer.current) window.clearTimeout(blurTimer.current);
              setOpen(true);
            }}
            onBlur={() => {
              blurTimer.current = window.setTimeout(() => setOpen(false), 150);
            }}
            onChange={(event) => {
              setQuery(event.target.value);
              setOpen(true);
              if (value && event.target.value !== value.nameVi) onSelect(null);
            }}
            onKeyDown={onKeyDown}
          />
          {value ? (
            <button
              type="button"
              className="taxonomy-autocomplete__clear"
              aria-label={`Xóa ${label}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                setQuery("");
                onSelect(null);
                setOpen(true);
              }}
            >
              Xóa
            </button>
          ) : null}
        </div>
        {hint ? <span className="ui-field__hint">{hint}</span> : null}
      </label>

      {open ? (
        <div className="taxonomy-autocomplete__panel">
          <div id={listboxId} role="listbox" aria-label={`Kết quả ${label}`}>
            {loading ? (
              <p className="taxonomy-autocomplete__state" role="status">Đang tải danh mục…</p>
            ) : error ? (
              <div className="taxonomy-autocomplete__state taxonomy-autocomplete__state--error" role="alert">
                <strong>Không tải được dữ liệu</strong>
                <span>{error}</span>
                {requestId ? <code>Mã yêu cầu: {requestId}</code> : null}
              </div>
            ) : items.length === 0 ? (
              <p className="taxonomy-autocomplete__state">Không có kết quả phù hợp.</p>
            ) : (
              items.map((item, index) => (
                <button
                  key={item.id}
                  id={`${listboxId}-${item.id}`}
                  type="button"
                  role="option"
                  aria-selected={index === activeIndex}
                  className="taxonomy-autocomplete__option"
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => choose(item)}
                >
                  <span>
                    <strong>{item.nameVi}</strong>
                    {item.nameEn ? <small>{item.nameEn}</small> : null}
                  </span>
                  <code>{item.code}</code>
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
